import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getReverseCaps, ReverseAmounts, useReturnCalculation, ZERO_REVERSE } from "./hooks/useReturnCalculation";
import { buildReturnPayload } from "./hooks/buildReturnPayload";
import OrderSearchModal from "./components/OrderSearchModal";
import OrderItemsTable from "./components/OrderItemsTable";
import OrderDetailsCard from "./components/OrderDetailsCard";
import PaymentDetailsCard from "./components/PaymentDetailsCard";
import ReturnSummaryCard from "./components/ReturnSummaryCard";
import useOrders from "src/business-types/grocery/hooks/useOrders";
import { ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { notifyError } from "src/assets/toastService";
import { Order } from "src/shared/types";
import { reactAppVersion } from "src/features/auth/axiosConfig";

type RefundType = "Cash" | "Card" | "Adj.Outstanding";

export default function OrderReturn() {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState<Order | null>(null);
  const [returnQty, setReturnQty] = useState<Record<number, number>>({});
  const [reverse, setReverse] = useState<ReverseAmounts>(ZERO_REVERSE);
  const [refundType, setRefundType] = useState<RefundType>("Cash");
  const [openSearch, setOpenSearch] = useState(true);
  const [disableButton, setDisableButton] = useState(false);
  const [openPrint, setOpenPrint] = useState("");

  const { searchOrderId, orderReturn } = useOrders();
  const navigate = useNavigate();
  const totals = useReturnCalculation(order, returnQty, reverse);

  useEffect(() => {
    if (order) {
      // New interface: status lives in orders[0].status
      const orderStatus = order.orders?.[0]?.status?.toLowerCase() ?? "";
      if (orderStatus === "cancelled") {
        notifyError("This order has been cancelled. Cannot process return.");
        setOrder(null);
        setOpenSearch(true);
        setOrderId("");
      }
    }
  }, [order, returnQty, reverse]);

  const handleSearch = () => {
    if (!orderId) return;
    searchOrderId(orderId, (orders: Order[]) => {
      const o = orders[0];
      // New interface: phones inside customerDetails
      if (o?.customerDetails?.phones) {
        setOrder(o);
        setOpenSearch(false);
      }
    });
  };

  const handleSubmit = async () => {
    if (!order) return;
    const deliveredOrders = order.orders.filter((o) => o.status === "Delivered");
    if (deliveredOrders.length === 0) {
      notifyError("Your order is not delivered yet. Cannot process return.");
      setTimeout(() => {
        navigate(`/grocery/${reactAppVersion}/orders/${order.orderId}`);
      }, 300);
      return;
    }
    setDisableButton(true);
    const payload = buildReturnPayload(order, returnQty, totals, refundType, reverse);
    try {
      // New interface: orderId replaces Orderid
      await orderReturn(order.orderId, payload);
      setOpenPrint("return");
      setTimeout(() => {
        navigate(`/grocery/${reactAppVersion}/orders/${order.orderId}`);
      }, 300);
    } catch (err) {
      console.error(err);
      setDisableButton(false);
    }
  };

  return (
    <div className="px-4 py-1 h-full w-full">
      <OrderSearchModal open={openSearch} orderId={orderId} setOrderId={setOrderId} onSearch={handleSearch} onClose={() => setOpenSearch(false)} />

      <div className="grid grid-cols-6 gap-2 h-[90%]">
        <OrderItemsTable order={order} returnQty={returnQty} setReturnQty={setReturnQty} />

        <div className={`col-span-2 space-y-2 ${ThemeBasedColorMenubar()} p-1 border rounded-xl shadow-inner`}>
          <OrderDetailsCard order={order} onChangeOrder={() => setOpenSearch(true)} />

          <PaymentDetailsCard order={order} remaining={totals.remaining} />

          <ReturnSummaryCard order={order} totals={totals} reverse={reverse} setReverse={setReverse} refundType={refundType} setRefundType={setRefundType} />

          {order && (
            <button className="bg-green-500 text-white w-full py-2 rounded" onClick={handleSubmit} disabled={totals.qty === 0 || totals.refund < 0 || disableButton}>
              Order Return
            </button>
          )}

          {!order && (
            <div className="p-4 space-y-3">
              <p className="text-bold">Search Return Order ID</p>
              <input
                className="border p-2 rounded w-full"
                placeholder="Enter Order ID"
                value={orderId}
                onChange={(e) => {
                  const value = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 8);
                  setOrderId(value);
                }}
              />
              <div className="flex justify-end gap-2">
                <button onClick={handleSearch} className="bg-blue-500 text-white px-4 py-1 rounded">
                  Find Order
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
